import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChatEntry } from 'dragonmouse-shared/interfaces';

// Two orthogonal concepts drive every behavior in this hook:
//
//   1. `adjustSpacer()` — keeps the scroll content tall enough that the last
//      user message can sit at the top of the visible area. Pure DOM
//      measurement; no delta tracking. Whenever anything that affects layout
//      changes, recompute.
//
//   2. `isAtBottomRef` — when the user is at the bottom of the chat, content
//      changes auto-stick to the bottom (matches ChatGPT/Claude). When the
//      user scrolls up, autoscroll pauses; when they scroll back to the
//      bottom (or click the jump button), it resumes.
//
// The "phases" (user message pinned to top after submit → content fills space
// → follow-bottom takes over once content overflows → user message scrolls up
// out of view) emerge naturally from these two pieces. No phase tracking.

// Module-level cache of scroll state per chatId. Lets us restore the user's
// scroll position when this hook's component unmounts and remounts (entering/
// exiting prototype mode), instead of snapping back to the bottom. We save
// scrollBottom (distance from the end of content) rather than scrollTop so
// the restore is invariant to clientHeight changes (banner above the input
// appearing) and content prepended above (parent chat fetched async).
const savedScrollPositions = new Map<string, { scrollBottom: number; scrollHeight: number }>();

interface UseChatScrollOptions {
	containerRef: React.RefObject<HTMLDivElement>;
	spacerRef: React.RefObject<HTMLDivElement>;
	entries: ChatEntry[];
	activeChatId: string | null;
	lastChatMessage: string | null;
	shouldSkipAutoScroll?: () => boolean;
	isInitialized?: boolean;
}

interface UseChatScrollResult {
	isScrolledToBottom: boolean;
	isRestoring: boolean;
	onJumpToBottom: () => void;
}

export function useChatScroll({
	containerRef,
	spacerRef,
	entries,
	activeChatId,
	lastChatMessage,
	shouldSkipAutoScroll,
	isInitialized,
}: UseChatScrollOptions): UseChatScrollResult {
	const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
	const [isRestoring, setIsRestoring] = useState(false);

	// Autoscroll mode = user intent. ON when chat is freshly entered, when a
	// user message is just submitted, when the user clicks the jump-to-bottom
	// button, or when the user manually scrolls to the bottom. OFF when the
	// user manually scrolls away from the bottom.
	//
	// Critically: this is NOT derived from `scroll` events. `scroll` fires for
	// every layout-induced reflow and every programmatic scrollTop change too;
	// using it as the source of truth created a feedback loop (content grows
	// → scroll fires → atBottom flickers → autoscroll fires another scroll →
	// repeat). Instead we listen for user-input events directly: `wheel` and
	// `touchmove` mark a brief window during which subsequent scroll position
	// changes are user-driven, and we read at-bottom only inside that window.
	const isAtBottomRef = useRef(true);

	// Set true while a user-input event is actively driving scrolling. Reset
	// after a short idle window. While true, the scroll listener evaluates
	// at-bottom from the actual position; while false, it's quiet.
	const userScrollingRef = useRef(false);

	const activeChatIdRef = useRef(activeChatId);
	activeChatIdRef.current = activeChatId;

	// While restoring a saved position, async content can fire scroll events
	// that would overwrite the cached value. Gate scroll-listener saves on this.
	const suppressSaveUntilRef = useRef(0);

	const entriesLength = entries?.length ?? 0;

	// THE invariant. Spacer is sized so the last user message can scroll to top.
	// As content arrives below the user message, the spacer shrinks naturally
	// (because spacer.offsetTop grows). Once content fills the visible area,
	// the spacer is 0 and content overflows — scroll-to-bottom from then on
	// scrolls the user message up out of view, just like ChatGPT.
	const adjustSpacer = useCallback(() => {
		const container = containerRef.current;
		const spacer = spacerRef.current;
		if (!container || !spacer) return;
		const userMessages = container.querySelectorAll('.user-message');
		const lastUser = userMessages[userMessages.length - 1] as HTMLElement | undefined;
		if (!lastUser) {
			spacer.style.height = '0px';
			return;
		}
		const required = lastUser.offsetTop + container.clientHeight;
		const desired = Math.max(0, required - spacer.offsetTop);
		spacer.style.height = `${desired}px`;
	}, [containerRef, spacerRef]);

	const stickToBottom = useCallback(() => {
		const container = containerRef.current;
		if (!container) return;
		container.scrollTop = container.scrollHeight - container.clientHeight;
	}, [containerRef]);

	// On every render, keep the spacer correct and (if user is at bottom)
	// auto-stick. Cheap: a few measurements + one style assignment. No deps —
	// runs whenever the conversation re-renders, which catches every case
	// that affects layout: entries change, streaming text, status messages,
	// queue indicator changes, etc.
	useLayoutEffect(() => {
		adjustSpacer();
		if (isAtBottomRef.current) {
			stickToBottom();
		}
	});

	// Re-run on container resize (window resize, mobile keyboard, etc. —
	// anything that changes clientHeight without a React re-render).
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;
		const ro = new ResizeObserver(() => {
			adjustSpacer();
			if (isAtBottomRef.current) {
				stickToBottom();
			}
		});
		ro.observe(container);
		return () => ro.disconnect();
	}, [adjustSpacer, stickToBottom, containerRef]);

	// When a new user message lands, scroll it to the top of the visible area.
	// adjustSpacer ran first (above) so the spacer is sized correctly to allow
	// scrollTop to reach userMsg.offsetTop. autoscroll stays on so streamed
	// content follows the bottom (which initially keeps user msg at top, then
	// pushes it up as content overflows).
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useLayoutEffect(() => {
		if (entriesLength === 0) return;
		if (entries[entriesLength - 1].kind !== 'user') return;
		const container = containerRef.current;
		if (!container) return;
		const userMessages = container.querySelectorAll('.user-message');
		const lastUser = userMessages[userMessages.length - 1] as HTMLElement | undefined;
		if (!lastUser) return;
		container.scrollTo({ top: lastUser.offsetTop, behavior: 'smooth' });
		isAtBottomRef.current = true;
		setIsScrolledToBottom(true);
	}, [entriesLength]);

	// Scroll position tracking. Two responsibilities, with strict separation:
	//
	//   - At-bottom state (drives autoscroll + jump-button visibility): only
	//     updated when the user is actively scrolling, signaled by `wheel` /
	//     `touchmove` events. Programmatic scrolls and content-growth-induced
	//     reflows fire `scroll` events too, but we ignore them — they don't
	//     represent user intent.
	//
	//   - Saved-position cache (for restore on remount): always recorded.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		let userIdleTimer: ReturnType<typeof setTimeout> | null = null;
		const markUserScrolling = () => {
			userScrollingRef.current = true;
			if (userIdleTimer) clearTimeout(userIdleTimer);
			userIdleTimer = setTimeout(() => {
				userScrollingRef.current = false;
			}, 200);
		};

		const handleScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = container;

			// Always update saved-position for restore-on-remount.
			if (Date.now() >= suppressSaveUntilRef.current) {
				const id = activeChatIdRef.current;
				if (id) {
					savedScrollPositions.set(id, {
						scrollBottom: scrollHeight - scrollTop - clientHeight,
						scrollHeight,
					});
				}
			}

			// Only the user can change at-bottom state. Programmatic scrolls
			// (stickToBottom, jump-to-bottom smooth animation, scroll-into-view)
			// pass through silently.
			if (!userScrollingRef.current) return;

			const atBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 2;
			isAtBottomRef.current = atBottom;
			setIsScrolledToBottom(atBottom);
		};

		container.addEventListener('wheel', markUserScrolling, { passive: true });
		container.addEventListener('touchmove', markUserScrolling, { passive: true });
		container.addEventListener('scroll', handleScroll);
		return () => {
			container.removeEventListener('wheel', markUserScrolling);
			container.removeEventListener('touchmove', markUserScrolling);
			container.removeEventListener('scroll', handleScroll);
			if (userIdleTimer) clearTimeout(userIdleTimer);
		};
	}, []);

	// On chat-id change (or first init), restore the saved position if we have
	// one for this chat, otherwise scroll to the bottom. useLayoutEffect ensures
	// the scroll happens before paint to avoid a flicker at the default scrollTop.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useLayoutEffect(() => {
		if (shouldSkipAutoScroll && shouldSkipAutoScroll()) return;
		if (!isInitialized) return;
		if (!containerRef.current) return;

		const saved = activeChatId ? savedScrollPositions.get(activeChatId) : undefined;

		if (saved !== undefined) {
			suppressSaveUntilRef.current = Date.now() + 2000;

			const container = containerRef.current;
			const applyTarget = () => {
				if (!containerRef.current) return;
				const { scrollHeight, clientHeight } = containerRef.current;
				containerRef.current.scrollTop = Math.max(
					0,
					scrollHeight - clientHeight - saved.scrollBottom
				);
			};

			// Near-bottom: apply immediately and pin to the new bottom as content grows.
			if (saved.scrollBottom < 2) {
				applyTarget();
				isAtBottomRef.current = true;
				setIsScrolledToBottom(true);
				const deadline = Date.now() + 1500;
				let rafId = requestAnimationFrame(function pin() {
					applyTarget();
					if (Date.now() > deadline) return;
					rafId = requestAnimationFrame(pin);
				});
				return () => cancelAnimationFrame(rafId);
			}

			// Content already at saved size: restore immediately.
			if (container.scrollHeight >= saved.scrollHeight) {
				applyTarget();
				const { scrollTop, scrollHeight, clientHeight } = container;
				const atBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 2;
				isAtBottomRef.current = atBottom;
				setIsScrolledToBottom(atBottom);
				return;
			}

			// Content still loading: hide until scrollHeight reaches saved size, then
			// apply and reveal — avoids the visible content swap as parent chat loads.
			const prevVisibility = container.style.visibility;
			container.style.visibility = 'hidden';
			setIsRestoring(true);

			let rafId = 0;
			let done = false;
			const finish = () => {
				if (done) return;
				done = true;
				applyTarget();
				if (containerRef.current) {
					containerRef.current.style.visibility = prevVisibility;
					const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
					const atBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 2;
					isAtBottomRef.current = atBottom;
					setIsScrolledToBottom(atBottom);
				}
				setIsRestoring(false);
			};

			const deadline = Date.now() + 1500;
			const poll = () => {
				if (!containerRef.current) return;
				if (containerRef.current.scrollHeight >= saved.scrollHeight || Date.now() > deadline) {
					finish();
					return;
				}
				rafId = requestAnimationFrame(poll);
			};
			rafId = requestAnimationFrame(poll);

			return () => {
				if (rafId) cancelAnimationFrame(rafId);
				if (!done && containerRef.current) {
					containerRef.current.style.visibility = prevVisibility;
				}
				setIsRestoring(false);
			};
		}

		// No saved position: scroll to bottom and turn autoscroll on.
		containerRef.current.scrollTop = containerRef.current.scrollHeight;
		isAtBottomRef.current = true;
		setIsScrolledToBottom(true);
	}, [activeChatId, isInitialized]);

	// Scroll-to-bottom on init after a 500ms delay — accounts for thumbnails and
	// other async content. Skips if we have a saved position for this chat.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (shouldSkipAutoScroll && shouldSkipAutoScroll()) return;
		if (!isInitialized) return;
		if (activeChatId && savedScrollPositions.has(activeChatId)) return;
		if (!containerRef.current) return;
		const capturedId = activeChatId;
		const timeoutId = setTimeout(() => {
			if (!containerRef.current) return;
			if (activeChatIdRef.current !== capturedId) return;
			if (capturedId && savedScrollPositions.has(capturedId)) return;
			containerRef.current.scrollTop = containerRef.current.scrollHeight;
			isAtBottomRef.current = true;
			setIsScrolledToBottom(true);
		}, 500);
		return () => clearTimeout(timeoutId);
	}, [activeChatId, isInitialized]);

	const onJumpToBottom = useCallback(() => {
		const container = containerRef.current;
		if (!container) return;
		container.scrollTo({ top: container.scrollHeight - container.clientHeight, behavior: 'smooth' });
		isAtBottomRef.current = true;
		setIsScrolledToBottom(true);
	}, [containerRef]);

	// Keep lastChatMessage referenced so callers can detect streaming changes;
	// we don't need it ourselves because the no-deps useLayoutEffect already
	// re-runs adjustSpacer on every render.
	void lastChatMessage;

	return {
		isScrolledToBottom,
		isRestoring,
		onJumpToBottom,
	};
}
